/**
 * Thin wrapper over the Telegram WebApp surface (SPEC 11.3). Everything is
 * feature-detected, so the app also runs in a plain browser during development
 * and in the E2E tests, where `window.Telegram.WebApp` is stubbed.
 */
interface HapticFeedback {
  impactOccurred?: (style: 'light' | 'medium' | 'heavy') => void;
  notificationOccurred?: (type: 'error' | 'success' | 'warning') => void;
  selectionChanged?: () => void;
}

interface WebApp {
  initData?: string;
  colorScheme?: 'light' | 'dark';
  themeParams?: Record<string, string>;
  HapticFeedback?: HapticFeedback;
  expand?: () => void;
  ready?: () => void;
  disableVerticalSwipes?: () => void;
  enableVerticalSwipes?: () => void;
  openTelegramLink?: (url: string) => void;
  BackButton?: { show: () => void; hide: () => void; onClick: (cb: () => void) => void };
}

function webApp(): WebApp | undefined {
  return (window as unknown as { Telegram?: { WebApp?: WebApp } }).Telegram?.WebApp;
}

export function initTelegram(): void {
  const app = webApp();
  app?.ready?.();
  app?.expand?.();
  if (app?.colorScheme === 'dark') document.documentElement.classList.add('dark');
}

export function initData(): string {
  const fromSdk = webApp()?.initData;
  if (fromSdk) return fromSdk;
  // Telegram also puts the launch parameters in the URL hash.
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const fromHash = hash.get('tgWebAppData');
  if (fromHash) return fromHash;
  return import.meta.env.VITE_DEV_INIT_DATA ?? '';
}

/** True when the page was not opened from inside Telegram. */
export function isOutsideTelegram(): boolean {
  return initData() === '';
}

export const haptics = {
  pick: () => webApp()?.HapticFeedback?.impactOccurred?.('light'),
  over: () => webApp()?.HapticFeedback?.selectionChanged?.(),
  drop: () => webApp()?.HapticFeedback?.impactOccurred?.('medium'),
  limit: () => webApp()?.HapticFeedback?.notificationOccurred?.('warning'),
  done: () => webApp()?.HapticFeedback?.notificationOccurred?.('success'),
  error: () => webApp()?.HapticFeedback?.notificationOccurred?.('error'),
};

export function setVerticalSwipes(enabled: boolean): void {
  const app = webApp();
  if (enabled) app?.enableVerticalSwipes?.();
  else app?.disableVerticalSwipes?.();
}

export function openBotChat(username: string): void {
  webApp()?.openTelegramLink?.(`https://t.me/${username}`);
}

/** `startapp=card_<id>` / `startapp=envelope_day_<date>` (SPEC 10.1). */
export function startParam(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('tgWebAppStartParam') ?? params.get('startapp');
}
